<template>
<v-dialog
    v-model="display"
    scrollable
    width="950"
    ref="container">
    <v-card>
        <v-toolbar flat color="primary" dark dense>
            <v-toolbar-title>
                Customer Activity #{{activity.code_f}}
            </v-toolbar-title>
        </v-toolbar>
        <v-card-text class="mt-2">
            <v-row class="mt-1 mb-1" style="position: relative">
                <v-col
                    cols="12"
                    md="4"
                    >
                    <div class="d-flex justify-space-between align-start">
                        <customer-avatar
                            :name="customer.display_name"
                            :phone="customer.phone">
                        </customer-avatar>
                    </div>
                    <div class="d-flex flex-column">
                        <v-chip
                            class="mb-1"
                            color="light"
                            text-color="grey darken-1">
                            <v-icon left>mdi-pound</v-icon>
                            {{activity.code_f}}
                        </v-chip>
                        <v-chip
                            class="mb-1"
                            color="light"
                            text-color="grey darken-1">
                            <v-icon left>mdi-chat-processing</v-icon>
                            {{activity.touch_point}}
                        </v-chip>
                        <v-chip
                            class="mb-1"
                            color="light"
                            text-color="grey darken-1">
                            <v-icon left>mdi-shape-outline</v-icon>
                            {{activity.type}}
                        </v-chip>
                        <v-chip
                            v-if="dueDate"
                            class="mb-1"
                            color="light"
                            text-color="grey darken-1">
                            <v-icon left>mdi-clock</v-icon>
                            Due: {{formatTimestamp(dueDate)}}
                        </v-chip>
                    </div>
                    <v-divider class="mt-4 mb-4" ></v-divider>
                    <div>
                        <v-text-field
                            v-if="activity.assignee" 
                            v-model="activity.assignee"
                            :label="$t('Assinged To')"
                            clear-icon="mdi-close"
                            clearable>
                        </v-text-field>
                        <user-search
                            v-else
                            @change="handleUserSearchChange"
                            @click:clear="handleUserSearchClear"
                            :disabled="!isOpen"
                            :dense="false"
                            label="Assigned To" 
                            hide-details 
                            item-value="id" 
                            item-text="name"
                            v-model="form.assignee_id"
                        >
                        </user-search>
                        <v-sheet v-if="!isOpen" class="text-center pa-2 my-2" color="light" outlined rounded="xl">
                            {{ "Closed by " + closedBy + " at " + formatTimestamp(closedAt) }}
                        </v-sheet>
                        <v-btn
                            class="my-2"
                            v-else
                            max-width="100%"
                            block
                            depressed
                            rounded
                            color="error"
                            :loading="loading.closing"
                            :disabled="!isOpen || loading.closing"
                            @click="handleClose">
                            <v-icon left small v-if="isOpen">mdi-lock</v-icon>
                            <span>Close</span>
                        </v-btn>
                        <v-btn
                            :disabled="!isOpen"
                            class="my-2"
                            depressed
                            rounded
                            block
                            @click="openEditSpecialDialog">
                            <v-icon left>mdi-calendar</v-icon>
                            {{dueDate ? "Change Due Date" : "Set Due Date"}}
                        </v-btn>
                    </div>
                </v-col>
                <v-col cols="12" md="8">

                    <div class="d-flex align-end justify-space-between mb-2">
                        <div class="text-h5">{{activity.summary}}</div>
                        <div>
                            <div class="text-caption">{{formatTimestamp(activity.created_at)}}</div>
                            <div class="text-caption">{{activity.created_by}}</div>
                        </div>
                    </div>
                    <div class="text-body-1">{{activity.details}}</div>

                    <!-- Comments Section -->
                    <div class="mt-4">
                        <div  style="max-height:300px; overflow-y: auto;" ref="commentsContainer">
                            <div v-if="comments.length > 0 && !loading.comments">
                                <customer-activity-display-comment-item v-for="comment in comments" :key="comment.id" :comment="comment"></customer-activity-display-comment-item>
                            </div>
                            <div v-else-if="loading.comments" class="text-center text-body-1 pa-4">
                                <v-progress-circular
                                    indeterminate
                                    color="primary">
                                </v-progress-circular>
                                <div class="text-caption">Loading Comments ...</div>
                            </div>
                            <div v-else class="text-center text-body-1 pa-4">
                                <div>No Comments</div>
                                <div v-if="isOpen" class="text-caption">Be the first one to comment on this activity!</div>
                            </div>
                        </div>
                        <v-form ref="form" v-if="isOpen">
                            <customer-activity-display-comment-edit v-model="form.comment" @submit="handleCommentSubmit" :loading="loading.submit"></customer-activity-display-comment-edit>
                        </v-form>
                    </div>

                </v-col>
            </v-row>
        </v-card-text>
        <v-card-actions>
            <v-btn text @click="display = false">{{$t("close")}}</v-btn>
        </v-card-actions>
    </v-card>
    <customer-activity-edit-special
        :activity="activity"
        :due-date="dueDate"
        v-if="editSpecialShown"
        v-model="editSpecialShown"
        @update="handleUpdate">
    </customer-activity-edit-special>
    <p-alert
        v-model="alert.display"
        v-if="alert.display"
        v-bind="alert"
        ></p-alert>
    <DecistionDialog
        ref="decisionDialog"
        @submit="onSubmitDecision"
        >
    </DecistionDialog>
</v-dialog>
</template>

<script>
import moment from 'moment';

import { getDpk } from '../../services/sales';
import { assignTo, closeCustomerActivity, createCustomerActivityComment, getCustomerActivityComments } from '../../services/sales/customerActivity';
import CustomerActivityDisplayCommentEdit from './CustomerActivityDisplayCommentEdit.vue';
import CustomerActivityDisplayCommentItem from './CustomerActivityDisplayCommentItem.vue';
import CustomerAvatar from './CustomerAvatar.vue';
import CustomerActivityEditSpecial from './CustomerActivityEditSpecial';
import PAlert from '../common/PAlert.vue';
import UserSearch from '../searchComponents/UserSearch.vue';
import DecistionDialog from '../common/DecisionDialog.vue';

export default {
    components: {
        CustomerActivityEditSpecial,
        CustomerActivityDisplayCommentEdit,
        CustomerActivityDisplayCommentItem,
        CustomerAvatar,
        PAlert,
        UserSearch,
         DecistionDialog
    },
    name: "CustomerActivityDisplay",
    data() {
        return {
            loading: {
                comments: false,
                submit: false,
                closing: false
            },
            form: {
                comment: null,
                assignee_id: null
            },
            comments: [],
            dpk: null,
            isOpen: true,
            closedBy: null,
            closedAt: null,
            dueDate: null,
            editSpecialShown: false,
            alert: {},
        }
    },
    props: {
        value: {
            required: true,
            type: Boolean
        },
        activity: {
            required: true,
            type: Object
        },
        customer: {
            type: Object,
            required: true
        }
    },
    computed: {
        display: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit("input", value);
            }
        }
    },
    created() {
        this.isOpen = this.activity.is_open;    
        this.closedBy = this.activity.closed_by;
        this.closedAt = this.activity.closed_at;
        this.dueDate = this.activity.due_date;
        this.fetchCustomerActivityComments();
        this.freshDpk();
    },
    methods: {
        formatTimestamp(timestamp) {
            return moment(timestamp).format("YYYY-MM-DD HH:MM");
        },
        freshDpk() {
            getDpk().then(response  => this.dpk = response.data.key);
        },
        handleCommentSubmit() {
            this.loading.submit = true;
            createCustomerActivityComment(this.activity.id, this.form, this.dpk)
            .then( response => {
                this.dpk = this.freshDpk();
                this.$refs.form.reset();
                this.comments.push(response.data.record);
            }).finally(() => {
                this.loading.submit = false;
            });

            console.log(this.form.comment);
        },
        fetchCustomerActivityComments() {
            this.loading.comments = true
            getCustomerActivityComments(this.activity.id)
            .then(response => this.comments = response.data)
            .finally(() => this.loading.comments = false);
        },
        openAlert({content, title, callback}) {
            this.alert = {...this.alert, content, title, display: true, callback}
        },
        handleClose() {
            this.openAlert({
                title: "Please Confirm",
                content: "Are you sure you want to set this activity as closed?",
                callback: this.setActivityClosed
            })
        },
        setActivityClosed() {
            this.loading.closing = true;
            closeCustomerActivity(this.activity.id)
            .then((response) => {
                this.isOpen = false;
                this.closedBy = response.data.record.closed_by;
                this.closedAt = response.data.record.closed_at;
                this.$emit("updated");
            })
            .finally(() => this.loading.closing = false);
        },
        scrollToEnd() {
            let container = this.$refs.commentsContainer;
            container.scrollTop = container.scrollHeight + 100;
        },
        openEditSpecialDialog() {
            this.editSpecialShown = true;
        },
        handleUpdate({record, comment}) {
            this.comments.push(comment);
            this.dueDate = record.due_date;
            this.$emit("updated");
        },
        handleUserSearchClear() {
            this.form.assignee_id = null
        },
        handleUserSearchChange() {
            this.openDecisionDialog();
        },
        openDecisionDialog() {
            let params = { 
                           title: this.$t('confirm_title'),
                           content: this.$t('Are you sure assigned'),
                           target: 'assign',
                           id: this.activity.id,
                           payload: {assignee_id: this.form.assignee_id}
                        };
            this.$refs.decisionDialog.open(params);
        },
        onSubmitDecision(params) {
            assignTo(params.id, params.payload).then(response => {
                this.handleUpdate({...response.data});
            });
            console.log(params);
        }
    },
    updated() {
        this.scrollToEnd();
    }
}
</script>
